import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { WidgetConfig } from '@/config/dashboard.types';
import { dashboardApi, type WidgetDataEnvelope } from '@/lib/api';
import { useSettingsHydrationStore } from '@/stores/settings-hydration';

import { queryKeys } from './query-keys';

/**
 * First re-ask while the warehouse has not built a widget's scope. A `collecting`
 * answer is expected to change on its own once Dataform runs, so a widget must
 * not sit on it until the next remount.
 */
export const COLLECTING_REFETCH_MS = 60_000;

/**
 * Ceiling of the back-off. A tenant with no files legitimately stays collecting
 * on Files Covered until its first file lands, so the poll cannot stay at once a
 * minute for every open dashboard.
 */
export const COLLECTING_REFETCH_MAX_MS = 30 * 60_000;

/** The slice of a TanStack query the resolver reads. */
export interface RefetchQueryState {
  queryHash: string;
  state: { data?: WidgetDataEnvelope; dataUpdatedAt: number };
}

interface CollectingStreak {
  updatedAt: number;
  count: number;
  seenAt: number;
}

/**
 * Consecutive collecting answers per query. Kept outside the query because its
 * own dataUpdateCount spans the whole lifetime, so a widget that had data and
 * later reads collecting would start the back-off near the ceiling.
 */
const collectingStreaks = new Map<string, CollectingStreak>();

/**
 * An entry is dropped when its widget answers with data, but a widget unmounted
 * while collecting (a dashboard edit, a filter change that rekeys the query)
 * never gets that answer and would leave its entry behind for the session. Any
 * entry older than a full poll at the ceiling belongs to a query that is no
 * longer asking, so the streak it holds is meaningless anyway.
 */
const STREAK_TTL_MS = 2 * COLLECTING_REFETCH_MAX_MS;

function pruneStreaks(streaks: Map<string, CollectingStreak>, now: number): void {
  for (const [hash, streak] of streaks) {
    if (now - streak.seenAt > STREAK_TTL_MS) streaks.delete(hash);
  }
}

/**
 * Polls a collecting widget until it converges, doubling from one minute per
 * consecutive collecting answer up to the ceiling and resetting on the first
 * data answer; otherwise honours the widget's own refresh interval, or none.
 */
export function widgetRefetchInterval(
  config: WidgetConfig,
  streaks: Map<string, CollectingStreak> = collectingStreaks
): (query: RefetchQueryState) => number | false {
  return (query) => {
    // Swept before the status check: an abandoned entry is only ever reclaimed by
    // some other query's evaluation, and a dashboard where nothing is collecting
    // any more would never run the sweep at all.
    const now = Date.now();
    pruneStreaks(streaks, now);
    if (query.state.data?.status !== 'collecting') {
      streaks.delete(query.queryHash);
      return config.dataSource?.refreshInterval ?? false;
    }
    const previous = streaks.get(query.queryHash);
    // Re-evaluations of the same answer must not advance the streak; only a new fetch does.
    const count =
      previous === undefined
        ? 1
        : previous.updatedAt === query.state.dataUpdatedAt
          ? previous.count
          : previous.count + 1;
    streaks.set(query.queryHash, { updatedAt: query.state.dataUpdatedAt, count, seenAt: now });
    return Math.min(COLLECTING_REFETCH_MS * 2 ** (count - 1), COLLECTING_REFETCH_MAX_MS);
  };
}

export function useWidgetDataQuery(
  config: WidgetConfig,
  globalFilters: Record<string, string>,
  widgetFilters: Record<string, string>,
  /** False while a filter value may still change, so the widget asks once. */
  ready = true
): UseQueryResult<WidgetDataEnvelope> {
  const combinedFilters = { ...globalFilters, ...widgetFilters };
  // Until the admin's saved filters are in the stores, any fetch is one the
  // hydration re-keys and throws away (TDEV-1112).
  const settingsHydrated = useSettingsHydrationStore((s) => s.hydrated);

  return useQuery({
    queryKey: queryKeys.widget.data(config.id, combinedFilters),
    queryFn: ({ signal }) =>
      dashboardApi.getWidgetData(config.dataSource?.endpoint ?? '', combinedFilters, signal),
    enabled: !!config.dataSource && settingsHydrated && ready,
    staleTime: 5 * 60 * 1000,
    refetchInterval: widgetRefetchInterval(config),
  });
}
